import { DatabaseService } from './database.service';
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { ObserverService } from './observer.service';
import { APIService } from './api.service';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { OrganisationService } from './organisation.service';
import { StorageService } from './storage.service';

@Injectable()
export class PersonService {

    constructor(
        public alertController: AlertController,
        private router: Router,
        private authService: AuthService,
        private observerService: ObserverService,
        private apiService: APIService,
        private databaseService: DatabaseService,
        private organisationService: OrganisationService,
        private storageService: StorageService
    ) { }

    isPersonUnique(person_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.person_person.where('id').equals(person_id).count(function(count) {
                if(count == 0) {
                    resolve(true);
                }
                else {
                    resolve(false);
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addPerson(raw_person) {
        return new Promise<void>((resolve, reject) => {
            this.isPersonUnique(raw_person.id).then((unique) => {
                if(unique) {
                    let person = {};
                    person["id"] = raw_person.id;
                    person["allow_on_account_payment_method"] = raw_person.allow_on_account_payment_method;
                    person["number"] = raw_person.number;
                    person["pricing_tier_id"] = raw_person.pricing_tier.id;
                    if(raw_person.first_name) {
                        person["first_name"] = raw_person.first_name;
                    }
                    if(raw_person.last_name) {
                        person["last_name"] = raw_person.last_name;
                    }
                    if(raw_person.full_name) {
                        person["full_name"] = raw_person.full_name;
                    }
                    if(raw_person.job_title) {
                        person["job_title"] = raw_person.job_title;
                    }
                    if(raw_person.email_address) {
                        person["email_address"] = raw_person.email_address;
                    }
                    if(raw_person.mobile_phone_number) {
                        person["mobile_phone_number"] = raw_person.mobile_phone_number;
                    }
                    if(raw_person.home_phone_number) {
                        person["home_phone_number"] = raw_person.home_phone_number;
                    }
                    if(raw_person.work_phone_number) {
                        person["work_phone_number"] = raw_person.work_phone_number;
                    }
                    if(raw_person.organisation) {
                        person["organisation_id"] = raw_person.organisation.id;
                    }
                    if(raw_person.amount_owing) {
                        person["amount_owing"] = raw_person.amount_owing;
                    }
                    if(raw_person.credit_limit) {
                        person["credit_limit"] = raw_person.credit_limit;
                    }
                    this.databaseService.person_person.add(person).then(() => {
                        this.getPerson(raw_person.id).then((person) => {
                            this.observerService.publish("person_added", person);
                            this.storageService.set("people_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else{
                    // NOTE: soft fail, websocket add has beaten us to it
                    resolve();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    bulkAddPeople(raw_people) {
        return new Promise<void>((resolve, reject) => {
            if(raw_people !== undefined) {
                this.databaseService.person_person.clear().then(async () => {
                    let people = [];
                    for (let i = 0; i < raw_people.length; i++) {
                        let person = {};
                        person["id"] = raw_people[i].id;
                        person["allow_on_account_payment_method"] = raw_people[i].allow_on_account_payment_method;
                        person["number"] = raw_people[i].number;
                        person["pricing_tier_id"] = raw_people[i].pricing_tier.id;
                        if(raw_people[i].first_name) {
                            person["first_name"] = raw_people[i].first_name;
                        }
                        if(raw_people[i].last_name) {
                            person["last_name"] = raw_people[i].last_name;
                        }
                        if(raw_people[i].full_name) {
                            person["full_name"] = raw_people[i].full_name;
                        }
                        if(raw_people[i].job_title) {
                            person["job_title"] = raw_people[i].job_title;
                        }
                        if(raw_people[i].email_address) {
                            person["email_address"] = raw_people[i].email_address;
                        }
                        if(raw_people[i].mobile_phone_number) {
                            person["mobile_phone_number"] = raw_people[i].mobile_phone_number;
                        }
                        if(raw_people[i].home_phone_number) {
                            person["home_phone_number"] = raw_people[i].home_phone_number;
                        }
                        if(raw_people[i].work_phone_number) {
                            person["work_phone_number"] = raw_people[i].work_phone_number;
                        }
                        if(raw_people[i].organisation) {
                            person["organisation_id"] = raw_people[i].organisation.id;
                        }
                        if(raw_people[i].amount_owing) {
                            person["amount_owing"] = raw_people[i].amount_owing;
                        }
                        if(raw_people[i].credit_limit) {
                            person["credit_limit"] = raw_people[i].credit_limit;
                        }
                        people.push(person);
                    }
                    await this.databaseService.person_person.bulkAdd(people).then(() => {
                        this.observerService.publish("people_changed", null);
                        this.storageService.set("people_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});  
                        resolve();
                    })
                    .catch((error) => {
                        reject(error);
                    });
                })
                .catch((error) => {
                    reject(error);
                });
            }
            else {
                resolve();
            }
        });
    }

    deletePerson(person) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.person_person.delete(person.id).then(() => {
                this.observerService.publish("person_deleted", person);
                this.storageService.set("people_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    getPeople() {
        return new Promise((resolve, reject) => {
            this.databaseService.person_person.toArray().then((people) => {
                let organisation_lookup = {};
                this.organisationService.getOrganisations().then((organisations: any) => {
                    for (let i = 0; i < organisations.length; i++) {
                        let organisation = organisations[i];
                        organisation_lookup[organisation.id] = organisation;
                    }
                    for (let i = 0; i < people.length; i++) {
                        if (people[i].organisation_id) {
                            people[i]['organisation'] = organisation_lookup[people[i].organisation_id];
                        }
                    }
                    resolve(people);
                })
                .catch(() => {
                    reject();
                });
            })
            .catch(() => {
                reject();
            });
        });
    }

    // TODO: Performance improvement, we could pass an options param and remove the need for getOrganisation on every call (like we do for getOrganisation)
    getPerson(person_id) {
        return new Promise((resolve, reject) => {
            this.databaseService.person_person.get(person_id).then((person) => {
                if(person.organisation_id) {
                    this.organisationService.getOrganisation(person.organisation_id,{}).then((organisation: any) => {
                        if(organisation) {
                            person['organisation'] = organisation;
                        }
                        resolve(person);
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    resolve(person);
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    getPersonCreditDetails(person_id, currency_id) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPersonCreditDetails(person_id, currency_id).then((credit_details: any) => {
                    if(credit_details) {
                        resolve(credit_details);
                    }
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to retrieve person credit details.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view person credit details.");
                    } 
                    else if(error.status == 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve person credit details at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getPersonFavourites(person_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPersonFavourites(person_id).then((favourites) => {
                    if (favourites) {
                        resolve(favourites);
                    }
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to retrieve person favourites.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view person favourites.");
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve person favourites at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getPersonStoreCredit(person_id, currency_id) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPersonStoreCredit(person_id, currency_id).then((store_credit: any) => {
                    if(store_credit) {
                        resolve(store_credit);
                    }
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to retrieve person store credit.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view person store credit.");
                    } 
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve person store credit at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getPersonMemberships(person_id, params) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getPersonMemberships(person_id, params).then((memberships: any) => {
                    if(memberships) {
                        resolve(memberships);
                    }
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to retrieve person memberships.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view person memberships.");
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve person memberships at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updatePerson(raw_person) {
        return new Promise<void>((resolve, reject) => {
            this.getPerson(raw_person.id).then((person) => {
                if(person) {
                    let person = {};
                    person["id"] = raw_person.id;
                    person["number"] = raw_person.number;
                    person["pricing_tier_id"] = raw_person.pricing_tier.id;
                    person["allow_on_account_payment_method"] = null;
                    person["organisation_id"] = null;
                    person["first_name"] = null;
                    person["last_name"] = null;
                    person["full_name"] = null;
                    person["email_address"] = null;
                    person["mobile_phone_number"] = null;
                    person["work_phone_number"] = null;
                    person["home_phone_number"] = null;
                    if (raw_person.allow_on_account_payment_method) {
                        person["allow_on_account_payment_method"] = raw_person.allow_on_account_payment_method;
                    }
                    if(raw_person.first_name) {
                        person["first_name"] = raw_person.first_name;
                    }
                    if(raw_person.last_name) {
                        person["last_name"] = raw_person.last_name;
                    }
                    if(raw_person.full_name) {
                        person["full_name"] = raw_person.full_name;
                    }
                    if(raw_person.email_address) {
                        person["email_address"] = raw_person.email_address;
                    }
                    if(raw_person.mobile_phone_number) {
                        person["mobile_phone_number"] = raw_person.mobile_phone_number;
                    }
                    if(raw_person.home_phone_number) {
                        person["home_phone_number"] = raw_person.home_phone_number;
                    }
                    if(raw_person.work_phone_number) {
                        person["work_phone_number"] = raw_person.work_phone_number;
                    }
                    if (raw_person.organisation) {
                        person["organisation_id"] = raw_person.organisation.id;
                    }
                    if(raw_person.amount_owing) {
                        person["amount_owing"] = raw_person.amount_owing;
                    }
                    if(raw_person.credit_limit) {
                        person["credit_limit"] = raw_person.credit_limit;
                    }
                    this.databaseService.person_person.update(raw_person.id, person).then(() => {
                        this.getPerson(raw_person.id).then((person) => {
                            this.observerService.publish("person_updated", person);
                            this.storageService.set("people_last_updated_date", new Date().toISOString()).then(() => {}).catch(() => {});
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    })
                    .catch(() => {
                        reject();
                    });
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updatePeoplePricingTier(old_pricing_tier_id, new_pricing_tier_id) {
        return new Promise<void>((resolve, reject) => {
            this.databaseService.person_person.where('pricing_tier_id').equals(old_pricing_tier_id).modify({ pricing_tier_id: new_pricing_tier_id }).then(() => {
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

}
